<template>
    <div class="wasa-root">
        <div class="wasa-page">

            <!-- Hero -->
            <div class="wasa-hero">
                <div class="wasa-container">
                    <div class="row g-0 align-items-center">
                        <div class="col-12">
                            <div class="d-flex align-items-center gap-2 mb-2">
                                <div class="wasa-logo-circle flex-shrink-0">
                                    <i class="fas fa-tint"></i>
                                </div>
                                <div class="overflow-hidden">
                                    <h2 class="wasa-hero-title mb-0">WASA সাধারণ আবেদন পোর্টাল</h2>
                                    <p class="wasa-hero-sub mb-0">Bangladesh Water Supply & Sewerage Authority</p>
                                </div>
                            </div>
                            <div class="d-flex flex-wrap gap-2">
                                <span class="wasa-badge"><i class="fas fa-shield-alt me-1"></i>নিরাপদ</span>
                                <span class="wasa-badge"><i class="fas fa-bolt me-1"></i>দ্রুত</span>
                                <span class="wasa-badge"><i class="fas fa-globe me-1"></i>লগইন ছাড়া</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Steps -->
            <div class="wasa-steps-bar">
                <div class="wasa-container">
                    <div class="row g-0">
                        <div class="col-12">
                            <div class="d-flex justify-content-center align-items-center gap-2 py-2">
                                <div class="wasa-step" :class="{ active: !submitted }">
                                    <span class="step-num">১</span>
                                    <span class="d-none d-sm-inline ms-1">আবেদন পূরণ</span>
                                </div>
                                <i class="fas fa-chevron-right wasa-step-divider"></i>
                                <div class="wasa-step" :class="{ active: submitted }">
                                    <span class="step-num">২</span>
                                    <span class="d-none d-sm-inline ms-1">নিশ্চিতকরণ</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Content -->
            <div class="wasa-container py-2 py-md-3">
                <div class="row justify-content-center g-0">
                    <div class="col-12">

                        <!-- Success -->
                        <div v-if="submitted" class="wasa-card text-center p-3 p-md-5">
                            <div class="success-icon-wrap mx-auto mb-3">
                                <i class="fas fa-check-circle"></i>
                            </div>
                            <h5 class="fw-bold text-success mb-1">আবেদন সফলভাবে জমা হয়েছে!</h5>
                            <p class="text-muted small mb-2">আপনার ট্র্যাকিং নম্বর</p>
                            <div class="tracking-no-box mx-auto mb-3">{{ submittedData.tracking_no }}</div>
                            <p class="text-muted small mb-3">
                                <i class="fas fa-info-circle me-1"></i>
                                এই নম্বরটি সংরক্ষণ করুন। পরবর্তীতে আবেদনের অবস্থা জানতে এটি প্রয়োজন হবে।
                            </p>
                            <div class="row g-2">
                                <div class="col-12 col-sm-6">
                                    <button class="btn wasa-btn-pdf w-100" @click="handleDownloadPdf">
                                        <i class="fas fa-file-pdf me-1"></i>PDF ডাউনলোড
                                    </button>
                                </div>
                                <div class="col-12 col-sm-6">
                                    <button class="btn wasa-btn-outline w-100" @click="resetForm">
                                        <i class="fas fa-plus me-1"></i>নতুন আবেদন
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Form -->
                        <div v-else class="wasa-card overflow-hidden">
                            <div class="wasa-form-header">
                                <i class="fas fa-file-alt me-2"></i>সাধারণ আবেদন ফর্ম
                            </div>

                            <div class="p-2 p-sm-3 p-lg-4">
                                <form @submit.prevent="handleSubmit">

                                    <!-- Type -->
                                    <div class="mb-3">
                                        <div class="wasa-section-title mb-2">
                                            <i class="fas fa-list-ul me-1"></i>আবেদনের ধরন
                                        </div>
                                        <div class="row g-2">
                                            <div
                                                v-for="(label, key) in applicationTypes"
                                                :key="key"
                                                class="col-4 col-sm-3 col-md-2"
                                            >
                                                <div
                                                    class="wasa-type-chip"
                                                    :class="{ selected: formData.application_type === key }"
                                                    @click="selectType(key)"
                                                >
                                                    <i :class="typeIcon(key)" class="chip-icon"></i>
                                                    <span class="chip-label">{{ label }}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <p v-if="!formData.application_type" class="wasa-hint mt-2 mb-0">
                                            <i class="fas fa-hand-point-up me-1"></i>আবেদনের ধরন নির্বাচন করুন
                                        </p>
                                    </div>

                                    <!-- Personal Info -->
                                    <div class="mb-4">
                                        <div class="wasa-section-title mb-2">
                                            <i class="fas fa-user me-1"></i>ব্যক্তিগত তথ্য
                                        </div>
                                        <div class="row g-2">
                                            <div class="col-12 col-sm-6">
                                                <div class="wasa-input-wrap">
                                                    <i class="fas fa-user wasa-input-icon"></i>
                                                    <input
                                                        type="text"
                                                        class="wasa-input"
                                                        v-model="formData.name"
                                                        placeholder="পূর্ণ নাম *"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div class="col-12 col-sm-6">
                                                <div class="wasa-input-wrap">
                                                    <i class="fas fa-mobile-alt wasa-input-icon"></i>
                                                    <input
                                                        type="tel"
                                                        class="wasa-input"
                                                        v-model="formData.mobile"
                                                        placeholder="মোবাইল নং * (01XXXXXXXXX)"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div class="col-12 col-sm-6">
                                                <div class="wasa-input-wrap">
                                                    <i class="fas fa-id-card wasa-input-icon"></i>
                                                    <input
                                                        type="text"
                                                        class="wasa-input"
                                                        v-model="formData.nid"
                                                        placeholder="জাতীয় পরিচয়পত্র নং (ঐচ্ছিক)"
                                                    />
                                                </div>
                                            </div>
                                            <div class="col-12 col-sm-6">
                                                <div class="wasa-input-wrap">
                                                    <i class="fas fa-map-marker-alt wasa-input-icon"></i>
                                                    <input
                                                        type="text"
                                                        class="wasa-input"
                                                        v-model="formData.address"
                                                        placeholder="বর্তমান ঠিকানা (ঐচ্ছিক)"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Dynamic Fields -->
                                    <transition name="fade-slide">
                                        <div v-if="formData.application_type && dynamicFields.length" class="mb-4">
                                            <div class="wasa-section-title mb-2">
                                                <i class="fas fa-clipboard-list me-1"></i>আবেদন সংক্রান্ত তথ্য
                                            </div>
                                            <div class="row g-2">
                                                <div
                                                    v-for="field in dynamicFields"
                                                    :key="field.key"
                                                    class="col-12 col-sm-6"
                                                >
                                                    <label class="wasa-label">
                                                        {{ field.label }}
                                                        <span v-if="field.required" class="text-danger">*</span>
                                                    </label>
                                                    <textarea
                                                        v-if="field.type === 'textarea'"
                                                        class="wasa-input"
                                                        style="padding-left: 10px; resize: vertical;"
                                                        v-model="dynamicValues[field.key]"
                                                        :placeholder="field.label + ' লিখুন'"
                                                        rows="3"
                                                        :required="field.required"
                                                    ></textarea>
                                                    <div v-else class="wasa-input-wrap">
                                                        <input
                                                            :type="field.type"
                                                            class="wasa-input"
                                                            v-model="dynamicValues[field.key]"
                                                            :placeholder="field.label + ' লিখুন'"
                                                            :required="field.required"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </transition>

                                    <!-- Footer -->
                                    <div class="pt-3 border-top">
                                        <div class="row g-2 align-items-center">
                                            <div class="col-12 col-sm-auto">
                                                <div class="location-pill" :class="formData.latitude ? 'got' : 'waiting'">
                                                    <i :class="formData.latitude ? 'fas fa-map-marker-alt' : 'fas fa-spinner fa-spin'"></i>
                                                    {{ formData.latitude ? 'অবস্থান পাওয়া গেছে' : 'অবস্থান নিচ্ছে...' }}
                                                </div>
                                            </div>
                                            <div class="col-12 col-sm">
                                                <button
                                                    type="submit"
                                                    class="btn wasa-btn-submit w-100"
                                                    :disabled="loading || !formData.application_type"
                                                >
                                                    <span v-if="loading">
                                                        <span class="spinner-border spinner-border-sm me-1"></span>জমা হচ্ছে...
                                                    </span>
                                                    <span v-else>
                                                        <i class="fas fa-paper-plane me-1"></i>আবেদন জমা দিন
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                </form>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <!-- Footer -->
            <footer class="wasa-footer">
                <div class="wasa-container text-center">
                    <p class="mb-1 fw-semibold small">Bangladesh Water Supply & Sewerage Authority (WASA)</p>
                    <p class="mb-0 x-small">© {{ new Date().getFullYear() }} সর্বস্বত্ব সংরক্ষিত | যোগাযোগ: wasa@gov.bd</p>
                </div>
            </footer>

        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, inject } from 'vue'
import handleWaterSupply from '@/services/modules/waterSupply'
import wasaApplicationPdf from '@/services/utils/wasaApplicationPdf'

const showError   = inject('showError')
const showSuccess = inject('showSuccess')

const { fetchApplicationTypes, fetchFormFields, submitGeneralApplication } = handleWaterSupply()
const { downloadPdf } = wasaApplicationPdf()

const loading          = ref(false)
const submitted        = ref(false)
const trackingNo       = ref('')
const applicationTypes = ref({})
const dynamicFields    = ref([])
const submittedData    = ref({})

const formData = reactive({
    application_type: '',
    name:             '',
    mobile:           '',
    nid:              '',
    address:          '',
    latitude:         '',
    longitude:        '',
})

const dynamicValues = reactive({})

const typeIcon = (key) => {
    const icons = {
        new_connection:      'fas fa-plug',
        reconnection:        'fas fa-redo',
        disconnection:       'fas fa-unlink',
        name_transfer:       'fas fa-exchange-alt',
        address_change:      'fas fa-map-pin',
        meter_change:        'fas fa-tachometer-alt',
        meter_test:          'fas fa-vial',
        pipe_size_change:    'fas fa-ruler',
        duplicate_bill:      'fas fa-copy',
        bill_correction:     'fas fa-edit',
        sewerage_connection: 'fas fa-water',
        complaint:           'fas fa-exclamation-circle',
    }
    return icons[key] || 'fas fa-circle'
}

const captureLocation = () => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
        (pos) => {
            formData.latitude  = String(pos.coords.latitude)
            formData.longitude = String(pos.coords.longitude)
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
}

const loadApplicationTypes = async () => {
    try {
        const res = await fetchApplicationTypes()
        if (res.status) applicationTypes.value = res.data
    } catch {
        showError('আবেদনের ধরন লোড করতে সমস্যা হয়েছে।')
    }
}

const selectType = async (key) => {
    formData.application_type = key
    dynamicFields.value = []
    Object.keys(dynamicValues).forEach(k => delete dynamicValues[k])
    try {
        const res = await fetchFormFields(key)
        if (res.status) dynamicFields.value = res.data.fields
    } catch {
        showError('ফর্ম ফিল্ড লোড করতে সমস্যা হয়েছে।')
    }
}

const handleSubmit = async () => {
    loading.value = true
    try {
        const res = await submitGeneralApplication({ ...formData, ...dynamicValues })
        if (!res.status) { showError(res.message); return }

        const typeLabel = applicationTypes.value[formData.application_type] || formData.application_type
        submittedData.value = {
            tracking_no:          res.tracking_no,
            name:                 formData.name,
            mobile:               formData.mobile,
            nid:                  formData.nid,
            address:              formData.address,
            created_at:           new Date().toISOString(),
            applicationType:      formData.application_type,
            applicationTypeLabel: typeLabel,
            generals: dynamicFields.value.map(f => ({ key: f.key, value: dynamicValues[f.key] || '' })),
            fieldLabels: Object.fromEntries(dynamicFields.value.map(f => [f.key, f.label])),
        }

        trackingNo.value = res.tracking_no
        submitted.value  = true
        showSuccess(res.message)
    } catch {
        showError('আবেদন জমা দিতে সমস্যা হয়েছে। আবার চেষ্টা করুন।')
    } finally {
        loading.value = false
    }
}

const handleDownloadPdf = () => {
    const d = submittedData.value
    downloadPdf(d, d.generals, d.fieldLabels, d.applicationTypeLabel)
}

const resetForm = () => {
    submitted.value           = false
    trackingNo.value          = ''
    formData.application_type = ''
    formData.name             = ''
    formData.mobile           = ''
    formData.nid              = ''
    formData.address          = ''
    formData.latitude         = ''
    formData.longitude        = ''
    dynamicFields.value       = []
    Object.keys(dynamicValues).forEach(k => delete dynamicValues[k])
    captureLocation()
}

onMounted(() => {
    loadApplicationTypes()
    captureLocation()
})
</script>

<style scoped>
/* isolate from theme */
.wasa-root {
    position: fixed;
    inset: 0;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 9999;
    background: #f0f4f8;
}

.wasa-page {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    width: 100%;
}

/* custom container — avoids theme .container overrides */
.wasa-container {
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
    margin-left: auto;
    margin-right: auto;
    box-sizing: border-box;
}
@media (min-width: 576px)  { .wasa-container { max-width: 540px;  padding-left: 16px; padding-right: 16px; } }
@media (min-width: 768px)  { .wasa-container { max-width: 720px; } }
@media (min-width: 992px)  { .wasa-container { max-width: 960px; } }
@media (min-width: 1200px) { .wasa-container { max-width: 1140px; } }

/* Hero */
.wasa-hero {
    background: linear-gradient(135deg, #0d47a1 0%, #1976d2 55%, #42a5f5 100%);
    padding: 18px 0 14px;
}
.wasa-logo-circle {
    width: 42px; height: 42px;
    background: rgba(255,255,255,0.2);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; color: #fff;
}
.wasa-hero-title {
    font-size: 1rem;
    font-weight: 700;
    color: #fff;
    line-height: 1.3;
    white-space: nowrap;
}
.wasa-hero-sub {
    font-size: 0.7rem;
    color: rgba(255,255,255,0.85);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.wasa-badge {
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.3);
    color: #fff;
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 0.7rem;
}
@media (min-width: 576px) {
    .wasa-hero { padding: 24px 0 18px; }
    .wasa-logo-circle { width: 52px; height: 52px; font-size: 22px; }
    .wasa-hero-title { font-size: 1.3rem; }
    .wasa-hero-sub   { font-size: 0.8rem; }
}
@media (min-width: 768px) {
    .wasa-hero { padding: 30px 0 22px; }
    .wasa-logo-circle { width: 58px; height: 58px; font-size: 26px; }
    .wasa-hero-title { font-size: 1.5rem; }
    .wasa-hero-sub   { font-size: 0.85rem; }
}

/* Steps */
.wasa-steps-bar {
    background: #fff;
    border-bottom: 1px solid #e3e8ef;
    box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}
.wasa-step {
    font-size: 0.82rem;
    color: #90a4ae;
    display: flex; align-items: center;
    transition: color 0.2s;
    white-space: nowrap;
}
.wasa-step.active { color: #1976d2; font-weight: 600; }
.step-num {
    width: 24px; height: 24px;
    border-radius: 50%;
    background: #e3e8ef;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.75rem; font-weight: 700; flex-shrink: 0;
}
.wasa-step.active .step-num { background: #1976d2; color: #fff; }
.wasa-step-divider { color: #b0bec5; font-size: 0.75rem; margin: 0 4px; }

/* Card */
.wasa-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}
.wasa-form-header {
    background: linear-gradient(90deg, #1565c0, #1e88e5);
    color: #fff;
    padding: 12px 16px;
    font-size: 0.9rem;
    font-weight: 600;
}

/* Section title */
.wasa-section-title {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #1976d2;
    border-left: 3px solid #1976d2;
    padding-left: 8px;
}

/* Type chips */
.wasa-type-chip {
    padding: 5px 3px;
    border-radius: 6px;
    border: 1.5px solid #cfd8dc;
    color: #546e7a;
    cursor: pointer;
    transition: all 0.2s;
    background: #f8fafc;
    user-select: none;
    text-align: center;
    line-height: 1.3;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
}
.chip-icon  { font-size: 0.82rem; }
.chip-label { font-size: 0.62rem; word-break: break-word; }

@media (min-width: 576px) {
    .wasa-type-chip { padding: 8px 6px; }
    .chip-icon      { font-size: 1rem; }
    .chip-label     { font-size: 0.72rem; }
}
@media (min-width: 768px) {
    .wasa-type-chip { padding: 10px 8px; border-radius: 8px; }
    .chip-icon      { font-size: 1.1rem; }
    .chip-label     { font-size: 0.8rem; }
}

.wasa-type-chip:hover  { border-color: #1976d2; color: #1976d2; background: #e3f2fd; }
.wasa-type-chip.selected {
    border-color: #1976d2;
    background: #1976d2;
    color: #fff;
    box-shadow: 0 2px 6px rgba(25,118,210,0.3);
}
.wasa-hint { font-size: 0.75rem; color: #90a4ae; }

/* Inputs */
.wasa-input-wrap {
    position: relative;
    display: flex; align-items: center;
}
.wasa-input-icon {
    position: absolute; left: 10px;
    color: #90a4ae; font-size: 0.8rem;
    pointer-events: none;
}
.wasa-input {
    display: block;
    width: 100%;
    padding: 9px 10px 9px 32px;
    border: 1.5px solid #cfd8dc;
    border-radius: 8px;
    font-size: 0.88rem;
    outline: none;
    background: #f8fafc;
    color: #37474f;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
}
.wasa-input:focus {
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25,118,210,0.12);
    background: #fff;
}
.wasa-label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: #546e7a;
    margin-bottom: 4px;
}

/* Location pill */
.location-pill {
    display: inline-block;
    font-size: 0.75rem;
    padding: 5px 12px;
    border-radius: 20px;
    white-space: nowrap;
}
.location-pill.got     { background: #e8f5e9; color: #2e7d32; }
.location-pill.waiting { background: #fff8e1; color: #f57f17; }

/* Buttons */
.wasa-btn-submit {
    background: linear-gradient(90deg, #1565c0, #1e88e5);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-size: 0.88rem;
    font-weight: 600;
    transition: opacity 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 8px rgba(25,118,210,0.25);
}
.wasa-btn-submit:hover:not(:disabled) { opacity: 0.9; }
.wasa-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; }

.wasa-btn-outline {
    border: 2px solid #1976d2;
    color: #1976d2;
    background: transparent;
    border-radius: 8px;
    padding: 8px 20px;
    font-weight: 600;
    font-size: 0.88rem;
    transition: all 0.2s;
}
.wasa-btn-outline:hover { background: #1976d2; color: #fff; }

.wasa-btn-pdf {
    background: linear-gradient(90deg, #b71c1c, #e53935);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 8px 20px;
    font-weight: 600;
    font-size: 0.88rem;
    box-shadow: 0 2px 8px rgba(183,28,28,0.25);
    transition: opacity 0.2s;
}
.wasa-btn-pdf:hover { opacity: 0.88; }

/* Success */
.success-icon-wrap {
    width: 68px; height: 68px;
    background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 34px; color: #2e7d32;
    box-shadow: 0 4px 16px rgba(46,125,50,0.2);
}
.tracking-no-box {
    display: inline-block;
    background: linear-gradient(90deg, #e3f2fd, #bbdefb);
    border: 2px dashed #1976d2;
    color: #0d47a1;
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: 2px;
    padding: 8px 20px;
    border-radius: 10px;
    word-break: break-all;
}

/* Footer */
.wasa-footer {
    background: #1a237e;
    color: #fff;
    padding: 16px 0;
    margin-top: auto;
}
.wasa-footer .x-small { font-size: 0.72rem; color: rgba(255,255,255,0.55); }

/* Transition */
.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter-from, .fade-slide-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
